/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 1: Platform & Host Profile
 * 
 * Features:
 * - MULTI-SELECT Platform selection (TikTok, IG, FB, YouTube, Shopee)
 * - Duration SLIDER with custom input
 * - CTA interval with CUSTOM option
 * - Session type (Solo, Co-Host, Interview)
 * - Host profile (Name, Age, Gender, Personality + Custom)
 */

import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';

// Minimalist one-color platform icons (SVG)
const PlatformIcons = {
  tiktok: ({ color = '#666' }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill={color}>
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  ),
  instagram: ({ color = '#666' }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill={color}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  facebook: ({ color = '#666' }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill={color}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  youtube: ({ color = '#666' }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill={color}>
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  shopee: ({ color = '#666' }) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill={color}>
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.6 4.2c2.4 0 3.6 1.5 3.6 3.3 0 .3-.3.6-.6.6s-.6-.3-.6-.6c0-1.2-.6-2.1-2.4-2.1-1.5 0-2.4.9-2.4 2.1 0 1.5 1.2 2.1 2.7 2.7 1.8.6 3.9 1.5 3.9 3.9 0 2.4-1.8 3.9-4.2 3.9-2.7 0-4.2-1.8-4.2-3.9 0-.3.3-.6.6-.6s.6.3.6.6c0 1.5 1.2 2.7 3 2.7 1.5 0 3-.9 3-2.7 0-1.5-1.2-2.1-2.7-2.7-1.8-.6-3.9-1.5-3.9-3.9C9 5.7 10.5 4.2 12.6 4.2z"/>
    </svg>
  )
};

// Platform options with minimalist icons
const PLATFORMS = [
  { id: 'tiktok_live', name: 'TikTok Live', iconKey: 'tiktok' },
  { id: 'ig_live', name: 'Instagram Live', iconKey: 'instagram' },
  { id: 'fb_live', name: 'Facebook Live', iconKey: 'facebook' },
  { id: 'youtube_live', name: 'YouTube Live', iconKey: 'youtube' },
  { id: 'shopee_live', name: 'Shopee Live', iconKey: 'shopee' }
];

// Session types
const SESSION_TYPES = [
  { id: 'solo', name: 'Solo Host', icon: '👤', desc: '1 person' },
  { id: 'co_host', name: 'Co-Host', icon: '👥', desc: '2 persons' },
  { id: 'interview', name: 'Interview/Guest', icon: '🎤', desc: 'Host + Guest' }
];

// Age ranges
const AGE_RANGES = ['18-25', '26-35', '36-45', '46-55', '56+'];

// Personality presets
const PERSONALITIES = [
  { id: 'energetic', name: 'Ceria & Energetic', icon: '🎉', desc: 'High energy, excited' },
  { id: 'santai', name: 'Santai & Cool', icon: '😎', desc: 'Relaxed, casual' },
  { id: 'professional', name: 'Professional & Formal', icon: '👔', desc: 'Corporate, polished' },
  { id: 'warm', name: 'Mesra & Warm', icon: '🤗', desc: 'Motherly, friendly' },
  { id: 'custom', name: 'Custom', icon: '✏️', desc: 'Define your own' }
];

// CTA preset options
const CTA_PRESETS = [
  { value: 5, label: 'Every 5 min' },
  { value: 10, label: 'Every 10 min' },
  { value: 15, label: 'Every 15 min' }
];

const Step1PlatformHost = ({ sessionId, sessionData, onComplete, onBack, setError }) => {
  const { language } = useLanguage();
  
  // Form state - MULTI-SELECT platforms
  const [platforms, setPlatforms] = useState(
    sessionData?.platforms || (sessionData?.platform ? [sessionData.platform] : [])
  );
  
  // Duration with slider and custom
  const [durationMinutes, setDurationMinutes] = useState(sessionData?.durationMinutes || 60);
  const [customDuration, setCustomDuration] = useState('');
  const [useCustomDuration, setUseCustomDuration] = useState(false);
  
  // CTA interval with custom option
  const [ctaInterval, setCtaInterval] = useState(sessionData?.ctaInterval || 5);
  const [customCtaInterval, setCustomCtaInterval] = useState('');
  const [useCustomCta, setUseCustomCta] = useState(false);
  
  const [sessionType, setSessionType] = useState(sessionData?.sessionType || 'solo');
  const [hosts, setHosts] = useState(sessionData?.hosts?.length > 0 ? sessionData.hosts : [
    { name: '', ageRange: '', gender: '', personality: '', personalityCustom: '', isPrimary: true }
  ]);
  
  const [errors, setErrors] = useState({});

  // Get text based on language
  const getText = (key) => {
    const translations = {
      en: {
        title: 'Platform & Host Setup',
        subtitle: 'Configure your live session settings and host profile',
        platformLabel: 'Target Platform(s) *',
        platformHint: 'Select all platforms you will stream to',
        durationLabel: 'Session Duration *',
        durationHint: 'Drag slider or enter custom duration',
        customDurationLabel: 'Custom (minutes)',
        ctaLabel: 'CTA Interval *',
        ctaHint: 'How often to remind viewers to take action',
        customCtaLabel: 'Custom (minutes)',
        sessionTypeLabel: 'Session Type *',
        hostProfileLabel: 'Host Profile',
        hostNameLabel: 'Host Name *',
        hostNamePlaceholder: 'e.g., Aina',
        hostAgeLabel: 'Host Age Range *',
        hostGenderLabel: 'Host Gender *',
        hostPersonalityLabel: 'Host Personality *',
        customPersonalityLabel: 'Custom Personality',
        customPersonalityPlaceholder: 'e.g., humble but confident',
        addHostBtn: '+ Add Host B',
        removeHostBtn: 'Remove',
        continueBtn: 'Continue to Objective →',
        errors: {
          platform: 'Please select at least one platform',
          hostName: 'Please enter host name',
          hostAge: 'Please select host age range',
          hostGender: 'Please select host gender',
          hostPersonality: 'Please select host personality'
        }
      },
      ms: {
        title: 'Platform & Tetapan Host',
        subtitle: 'Konfigurasikan tetapan sesi live dan profil host anda',
        platformLabel: 'Platform Sasaran *',
        platformHint: 'Pilih semua platform yang anda akan stream',
        durationLabel: 'Tempoh Sesi *',
        durationHint: 'Geser slider atau masukkan tempoh tersuai',
        customDurationLabel: 'Tersuai (minit)',
        ctaLabel: 'Selang CTA *',
        ctaHint: 'Berapa kerap ingatkan penonton untuk bertindak',
        customCtaLabel: 'Tersuai (minit)',
        sessionTypeLabel: 'Jenis Sesi *',
        hostProfileLabel: 'Profil Host',
        hostNameLabel: 'Nama Host *',
        hostNamePlaceholder: 'contoh: Aina',
        hostAgeLabel: 'Julat Umur Host *',
        hostGenderLabel: 'Jantina Host *',
        hostPersonalityLabel: 'Personaliti Host *',
        customPersonalityLabel: 'Personaliti Tersuai',
        customPersonalityPlaceholder: 'contoh: humble tapi confident',
        addHostBtn: '+ Tambah Host B',
        removeHostBtn: 'Buang',
        continueBtn: 'Teruskan ke Objektif →',
        errors: {
          platform: 'Sila pilih sekurang-kurangnya satu platform',
          hostName: 'Sila masukkan nama host',
          hostAge: 'Sila pilih julat umur host',
          hostGender: 'Sila pilih jantina host',
          hostPersonality: 'Sila pilih personaliti host'
        }
      },
      bm: {
        title: 'Platform & Tetapan Host',
        subtitle: 'Konfigurasikan tetapan sesi live dan profil host anda',
        platformLabel: 'Platform Sasaran *',
        platformHint: 'Pilih semua platform yang anda akan stream',
        durationLabel: 'Tempoh Sesi *',
        durationHint: 'Geser slider atau masukkan tempoh tersuai',
        customDurationLabel: 'Tersuai (minit)',
        ctaLabel: 'Selang CTA *',
        ctaHint: 'Berapa kerap ingatkan penonton untuk bertindak',
        customCtaLabel: 'Tersuai (minit)',
        sessionTypeLabel: 'Jenis Sesi *',
        hostProfileLabel: 'Profil Host',
        hostNameLabel: 'Nama Host *',
        hostNamePlaceholder: 'contoh: Aina',
        hostAgeLabel: 'Julat Umur Host *',
        hostGenderLabel: 'Jantina Host *',
        hostPersonalityLabel: 'Personaliti Host *',
        customPersonalityLabel: 'Personaliti Tersuai',
        customPersonalityPlaceholder: 'contoh: humble tapi confident',
        addHostBtn: '+ Tambah Host B',
        removeHostBtn: 'Buang',
        continueBtn: 'Teruskan ke Objektif →',
        errors: {
          platform: 'Sila pilih sekurang-kurangnya satu platform',
          hostName: 'Sila masukkan nama host',
          hostAge: 'Sila pilih julat umur host',
          hostGender: 'Sila pilih jantina host',
          hostPersonality: 'Sila pilih personaliti host'
        }
      },
      urban: {
        title: 'Platform & Host Setup',
        subtitle: 'Configure live session settings dan host profile',
        platformLabel: 'Target Platform(s) *',
        platformHint: 'Select all platforms yang you akan stream',
        durationLabel: 'Session Duration *',
        durationHint: 'Drag slider atau enter custom duration',
        customDurationLabel: 'Custom (minutes)',
        ctaLabel: 'CTA Interval *',
        ctaHint: 'How often nak remind viewers untuk take action',
        customCtaLabel: 'Custom (minutes)',
        sessionTypeLabel: 'Session Type *',
        hostProfileLabel: 'Host Profile',
        hostNameLabel: 'Host Name *',
        hostNamePlaceholder: 'e.g., Aina',
        hostAgeLabel: 'Host Age Range *',
        hostGenderLabel: 'Host Gender *',
        hostPersonalityLabel: 'Host Personality *',
        customPersonalityLabel: 'Custom Personality',
        customPersonalityPlaceholder: 'e.g., humble tapi confident',
        addHostBtn: '+ Add Host B',
        removeHostBtn: 'Remove',
        continueBtn: 'Continue ke Objective →',
        errors: {
          platform: 'Please pilih at least one platform',
          hostName: 'Please masukkan host name',
          hostAge: 'Please pilih host age range',
          hostGender: 'Please pilih host gender',
          hostPersonality: 'Please pilih host personality'
        }
      }
    };
    
    const lang = language || 'en';
    const translation = translations[lang] || translations.en;
    return translation[key] !== undefined ? translation[key] : (translations.en[key] || key);
  };

  // Get error text helper
  const getErrorText = (errorKey) => {
    const errors = getText('errors');
    if (typeof errors === 'object' && errors !== null) {
      return errors[errorKey] || errorKey;
    }
    return errorKey;
  };

  // Toggle platform selection (multi-select)
  const togglePlatform = (platformId) => {
    setPlatforms(prev => {
      if (prev.includes(platformId)) {
        return prev.filter(p => p !== platformId);
      } else {
        return [...prev, platformId];
      }
    });
    if (errors.platform) setErrors(prev => ({ ...prev, platform: null }));
  };

  // Format duration display
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hrs} hr`;
    return `${hrs} hr ${mins} min`;
  };

  // Handle slider change
  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setDurationMinutes(value);
    setUseCustomDuration(false);
    setCustomDuration('');
  };

  // Handle custom duration
  const handleCustomDuration = (value) => {
    setCustomDuration(value);
    const num = parseInt(value);
    if (num > 0 && num <= 240) {
      setDurationMinutes(num);
      setUseCustomDuration(true);
    }
  };

  // Handle custom CTA
  const handleCustomCta = (value) => {
    setCustomCtaInterval(value);
    const num = parseInt(value);
    if (num > 0 && num <= 60) {
      setCtaInterval(num);
      setUseCustomCta(true);
    }
  };

  // Update host field
  const updateHost = (index, field, value) => {
    const newHosts = [...hosts];
    newHosts[index] = { ...newHosts[index], [field]: value };
    setHosts(newHosts);
    
    if (errors[`host${index}_${field}`]) {
      setErrors(prev => ({ ...prev, [`host${index}_${field}`]: null }));
    }
  };

  // Add second host
  const addHost = () => {
    if (hosts.length < 2) {
      setHosts([...hosts, { name: '', ageRange: '', gender: '', personality: '', personalityCustom: '', isPrimary: false }]);
    }
  };

  // Remove host
  const removeHost = (index) => {
    if (hosts.length > 1) {
      setHosts(hosts.filter((_, i) => i !== index));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (platforms.length === 0) {
      newErrors.platform = getErrorText('platform');
    }
    
    hosts.forEach((host, idx) => {
      if (!host.name.trim()) {
        newErrors[`host${idx}_name`] = getErrorText('hostName');
      }
      if (!host.ageRange) {
        newErrors[`host${idx}_age`] = getErrorText('hostAge');
      }
      if (!host.gender) {
        newErrors[`host${idx}_gender`] = getErrorText('hostGender');
      }
      if (!host.personality) {
        newErrors[`host${idx}_personality`] = getErrorText('hostPersonality');
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = () => {
    if (!validate()) {
      setError(language === 'bm' ? 'Sila lengkapkan semua medan yang diperlukan' : 'Please complete all required fields');
      return;
    }
    
    setError(null);
    onComplete({
      platforms, // Array of selected platforms
      platform: platforms[0], // Primary platform for backward compatibility
      durationMinutes,
      ctaInterval,
      sessionType,
      hosts
    });
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ 
          fontSize: '22px',
          fontWeight: '700',
          marginBottom: EMZI_SPACING.sm,
          color: '#fff'
        }}>
          🎬 {getText('title')}
        </h1>
        <p style={{ 
          fontSize: '14px',
          color: 'rgba(255,255,255,0.9)'
        }}>
          {getText('subtitle')}
        </p>
      </div>

      {/* Platform Selection - MULTI-SELECT */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('platformLabel')}
          </CardTitle>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {getText('platformHint')}
          </p>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {PLATFORMS.map(p => {
              const isSelected = platforms.includes(p.id);
              const IconComponent = PlatformIcons[p.iconKey];
              return (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  style={{
                    padding: '16px 12px',
                    borderRadius: '10px',
                    border: isSelected ? '3px solid #F89B29' : '2px solid #e0e0e0',
                    background: isSelected ? '#FFF5E6' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  {/* Checkmark for selected */}
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      background: '#F89B29',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                  {/* Minimalist one-color icon */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginBottom: '8px',
                    opacity: isSelected ? 1 : 0.7
                  }}>
                    <IconComponent color={isSelected ? '#F89B29' : '#666'} />
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: isSelected ? '#F89B29' : '#333' }}>{p.name}</div>
                </button>
              );
            })}
          </div>
          {platforms.length > 0 && (
            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#E8F4FF', borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: '#0e67a2', fontWeight: '500' }}>
                ✅ Selected: {platforms.map(p => PLATFORMS.find(pl => pl.id === p)?.name).join(', ')}
              </span>
            </div>
          )}
          {errors.platform && (
            <p style={{ color: '#c00', fontSize: '12px', marginTop: '8px' }}>⚠️ {errors.platform}</p>
          )}
        </CardContent>
      </Card>

      {/* Duration with Slider & Custom */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('durationLabel')}
          </CardTitle>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {getText('durationHint')}
          </p>
        </CardHeader>
        <CardContent>
          {/* Duration Display */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '16px',
            padding: '12px',
            background: '#E8F4FF',
            borderRadius: '10px'
          }}>
            <span style={{ fontSize: '28px', fontWeight: '700', color: '#0e67a2' }}>
              {formatDuration(durationMinutes)}
            </span>
          </div>

          {/* Slider */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="range"
              min="15"
              max="180"
              step="5"
              value={durationMinutes}
              onChange={handleSliderChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: `linear-gradient(to right, #0e67a2 0%, #0e67a2 ${((durationMinutes - 15) / 165) * 100}%, #e0e0e0 ${((durationMinutes - 15) / 165) * 100}%, #e0e0e0 100%)`,
                appearance: 'none',
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999', marginTop: '4px' }}>
              <span>15 min</span>
              <span>1 hr</span>
              <span>2 hr</span>
              <span>3 hr</span>
            </div>
          </div>

          {/* Custom Duration Input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#666', whiteSpace: 'nowrap' }}>
              {getText('customDurationLabel')}:
            </label>
            <input
              type="number"
              min="15"
              max="240"
              value={customDuration}
              onChange={(e) => handleCustomDuration(e.target.value)}
              placeholder="e.g., 75"
              style={{
                flex: 1,
                padding: '10px 12px',
                border: useCustomDuration ? '2px solid #0e67a2' : '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '120px'
              }}
            />
            <span style={{ fontSize: '12px', color: '#999' }}>min</span>
          </div>
        </CardContent>
      </Card>

      {/* CTA Interval with Custom Option */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('ctaLabel')}
          </CardTitle>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {getText('ctaHint')}
          </p>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {CTA_PRESETS.map(c => (
              <button
                key={c.value}
                onClick={() => {
                  setCtaInterval(c.value);
                  setUseCustomCta(false);
                  setCustomCtaInterval('');
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: ctaInterval === c.value && !useCustomCta ? '2px solid #F89B29' : '1px solid #e0e0e0',
                  background: ctaInterval === c.value && !useCustomCta ? '#FFF5E6' : '#fff',
                  color: ctaInterval === c.value && !useCustomCta ? '#F89B29' : '#666',
                  fontWeight: ctaInterval === c.value && !useCustomCta ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '13px'
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Custom CTA Input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#666', whiteSpace: 'nowrap' }}>
              {getText('customCtaLabel')}:
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={customCtaInterval}
              onChange={(e) => handleCustomCta(e.target.value)}
              placeholder="e.g., 7"
              style={{
                flex: 1,
                padding: '10px 12px',
                border: useCustomCta ? '2px solid #F89B29' : '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: '100px'
              }}
            />
            <span style={{ fontSize: '12px', color: '#999' }}>min</span>
          </div>

          {/* Current Selection Display */}
          <div style={{ marginTop: '12px', padding: '8px 12px', background: '#FFF5E6', borderRadius: '8px' }}>
            <span style={{ fontSize: '12px', color: '#F89B29', fontWeight: '500' }}>
              🔔 CTA every {ctaInterval} minutes = {Math.floor(durationMinutes / ctaInterval)} reminders in your {formatDuration(durationMinutes)} session
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Session Type */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('sessionTypeLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '12px' }}>
            {SESSION_TYPES.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  setSessionType(s.id);
                  if (s.id === 'solo' && hosts.length > 1) {
                    setHosts([hosts[0]]);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '16px',
                  borderRadius: '10px',
                  border: sessionType === s.id ? '2px solid #0e67a2' : '1px solid #e0e0e0',
                  background: sessionType === s.id ? '#E8F4FF' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{s.icon}</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: sessionType === s.id ? '#0e67a2' : '#333' }}>
                  {s.name}
                </div>
                <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>{s.desc}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Host Profiles */}
      {hosts.map((host, idx) => (
        <Card key={`item-${idx}`} style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
          <CardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
                👤 {getText('hostProfileLabel')} {hosts.length > 1 ? (idx === 0 ? 'A (Primary)' : 'B') : ''}
              </CardTitle>
              {idx > 0 && (
                <button
                  onClick={() => removeHost(idx)}
                  style={{
                    padding: '6px 12px',
                    background: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '6px',
                    color: '#c00',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {getText('removeHostBtn')}
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Host Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '6px' }}>
                {getText('hostNameLabel')}
              </label>
              <input
                type="text"
                value={host.name}
                onChange={(e) => updateHost(idx, 'name', e.target.value)}
                placeholder={getText('hostNamePlaceholder')}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: errors[`host${idx}_name`] ? '2px solid #c00' : '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              {errors[`host${idx}_name`] && (
                <p style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>⚠️ {errors[`host${idx}_name`]}</p>
              )}
            </div>

            {/* Host Age Range */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '6px' }}>
                {getText('hostAgeLabel')}
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {AGE_RANGES.map(age => (
                  <button
                    key={age}
                    onClick={() => updateHost(idx, 'ageRange', age)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: host.ageRange === age ? '2px solid #0e67a2' : '1px solid #ddd',
                      background: host.ageRange === age ? '#0e67a2' : '#fff',
                      color: host.ageRange === age ? '#fff' : '#666',
                      fontWeight: host.ageRange === age ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {age}
                  </button>
                ))}
              </div>
              {errors[`host${idx}_age`] && (
                <p style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>⚠️ {errors[`host${idx}_age`]}</p>
              )}
            </div>

            {/* Host Gender */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '6px' }}>
                {getText('hostGenderLabel')}
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['male', 'female'].map(g => (
                  <button
                    key={g}
                    onClick={() => updateHost(idx, 'gender', g)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: host.gender === g ? '2px solid #0e67a2' : '1px solid #ddd',
                      background: host.gender === g ? '#E8F4FF' : '#fff',
                      color: host.gender === g ? '#0e67a2' : '#666',
                      fontWeight: host.gender === g ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {g === 'male' ? '👨 Lelaki' : '👩 Perempuan'}
                  </button>
                ))}
              </div>
              {errors[`host${idx}_gender`] && (
                <p style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>⚠️ {errors[`host${idx}_gender`]}</p>
              )}
            </div>

            {/* Host Personality */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '6px' }}>
                {getText('hostPersonalityLabel')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {PERSONALITIES.map(p => (
                  <button
                    key={p.id}
                    onClick={() => updateHost(idx, 'personality', p.id)}
                    style={{
                      padding: '12px 8px',
                      borderRadius: '8px',
                      border: host.personality === p.id ? '2px solid #0e67a2' : '1px solid #ddd',
                      background: host.personality === p.id ? '#E8F4FF' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>{p.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: host.personality === p.id ? '#0e67a2' : '#333' }}>
                      {p.name}
                    </div>
                  </button>
                ))}
              </div>
              {errors[`host${idx}_personality`] && (
                <p style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>⚠️ {errors[`host${idx}_personality`]}</p>
              )}
            </div>

            {/* Custom Personality Input */}
            {host.personality === 'custom' && (
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '6px' }}>
                  {getText('customPersonalityLabel')}
                </label>
                <input
                  type="text"
                  value={host.personalityCustom}
                  onChange={(e) => updateHost(idx, 'personalityCustom', e.target.value)}
                  placeholder={getText('customPersonalityPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Add Host Button (for Co-Host/Interview) */}
      {(sessionType === 'co_host' || sessionType === 'interview') && hosts.length < 2 && (
        <button
          onClick={addHost}
          style={{
            width: '100%',
            padding: '14px',
            background: '#fff',
            border: '2px dashed #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: EMZI_SPACING.lg
          }}
        >
          {getText('addHostBtn')}
        </button>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          style={{
            flex: 2,
            padding: '14px',
            background: '#F7941E',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {getText('continueBtn')}
        </button>
      </div>
    </div>
  );
};

export default Step1PlatformHost;
